// Angular
import { HttpErrorResponse } from '@angular/common/http';

/**
 * HTTP status returned when a filing draft or clerk-review filing is locked by
 * another user (RFC 4918 Locked).
 */
export const HTTP_STATUS_LOCKED = 423;

/**
 * Shape of a ResourceLock error entry from the API on HTTP 423.
 * `data` may use PascalCase keys (dictionary serialization) or camelCase.
 */
interface ResourceLockError {
  domain?: string;
  data?: {
    LockedByUserFirstName?: string;
    LockedByUserLastName?: string;
    lockedByUserFirstName?: string;
    lockedByUserLastName?: string;
  };
}

/**
 * True when {@link err} is an HTTP 423 resource-lock response.
 */
export function isResourceLocked(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === HTTP_STATUS_LOCKED;
}

/**
 * Normalizes an HTTP 423 error body into an array of error entries.
 * Accepts a bare array, `{ errors: [...] }`, a single entry object, or a JSON string.
 */
function normalizeLockErrorEntries(errorBody: unknown): ResourceLockError[] {
  let body = errorBody;

  if (typeof body === 'string') {
    try {
      body = JSON.parse(body);
    } catch {
      return [];
    }
  }

  if (Array.isArray(body)) {
    return body as ResourceLockError[];
  }

  if (body && typeof body === 'object') {
    const record = body as { errors?: unknown };
    if (Array.isArray(record.errors)) {
      return record.errors as ResourceLockError[];
    }
    return [body as ResourceLockError];
  }

  return [];
}

/**
 * Pulls the locking user's display name ("First Last") out of an HTTP 423
 * response body. Returns null when the body is not the expected shape or no
 * name parts are present (e.g. locked by a user in a different org).
 */
export function extractLockedByName(errorBody: unknown): string | null {
  for (const entry of normalizeLockErrorEntries(errorBody)) {
    const data = entry?.data;
    const first = (
      data?.LockedByUserFirstName ?? data?.lockedByUserFirstName
    )?.trim();
    const last = (
      data?.LockedByUserLastName ?? data?.lockedByUserLastName
    )?.trim();
    const name = [first, last].filter(Boolean).join(' ');
    if (name) {
      return name;
    }
  }

  return null;
}

/**
 * User-facing message for a locked filing / clerk-review transaction.
 * Includes the locking user's name when the API returns it (same-org lock).
 */
export function getResourceLockUserMessage(err: unknown): string {
  const lockedByName =
    err instanceof HttpErrorResponse ? extractLockedByName(err.error) : null;

  return lockedByName
    ? `This filing is currently locked by ${lockedByName}.`
    : 'This filing is currently locked by another user.';
}
